import {
  MaterialType,
  ClassificationInputName,
  ClassificationMetadataEntry,
  ClassificationMetadataValueSet
} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit/src/common/utils/testingUtils'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {ControlledAutocomplete, ControlledAutocompleteProps} from '../../../../../common/components'
import {MaterialFormDefaultValues} from '../../../materials-details/ExistingMaterialDetails/hooks'

export interface ClassificationAutocompleteProps<T extends MaterialType> {
  materialType: T
  inputName: ClassificationInputName<T>
  metadata: ClassificationMetadataEntry<T>
  isDisabled: boolean
  renderOption?: ControlledAutocompleteProps<string, MaterialFormDefaultValues>['renderOption']
  onChangeCb?: () => void
}

export const ClassificationAutocomplete = <T extends MaterialType>({
  materialType,
  inputName,
  metadata,
  isDisabled,
  renderOption,
  onChangeCb
}: ClassificationAutocompleteProps<T>) => {
  const {t} = useTranslation()
  const {control, watch} = useFormContext<MaterialFormDefaultValues>()

  const validValueSet = metadata.inputs[inputName].valueSets.find(
    (valueSet: ClassificationMetadataValueSet) => {
      // Agreement with BE: for now if valuesShownWnen is undefined, it's the only valid value set
      if (valueSet.valuesShownWhen === undefined) {
        return true
      }
      // Agreement with BE: for now any field can be dependant only on one other field
      const conditionForValueSet = valueSet.valuesShownWhen[0]

      const conditionCurrentFormValue = watch(
        `classification.${conditionForValueSet.inputName}`
      ) as string | null
      return conditionCurrentFormValue === conditionForValueSet.value
    }
  )
  const options = validValueSet?.values || []
  const isInputWithNoOptions = options.length === 0

  return (
    <ControlledAutocomplete
      control={control}
      formDataName={`classification.${inputName}`}
      options={options}
      renderOption={renderOption}
      label={t(`materialsSettings.classificationInputs.${materialType}.${inputName}`)}
      rules={{
        validate: (value) => isInputWithNoOptions || !!value || t('error.required')
      }}
      disabled={isDisabled || isInputWithNoOptions}
      onChangeCb={onChangeCb}
      {...dataTestId(`${materialType}_classification_${inputName}`)}
    />
  )
}
